const MAX_RESPONSE_BODY_LENGTH = 2000;
const BODY_READ_TIMEOUT_MS = 3000;

const isResponse = (error: unknown): error is Response =>
  typeof Response !== 'undefined' && error instanceof Response;

/**
 * Reads at most MAX_RESPONSE_BODY_LENGTH characters from the response body
 * without buffering the full stream, giving up after BODY_READ_TIMEOUT_MS
 * so a slow or streaming response cannot stall the caller.
 */
const readBodyBounded = async (
  response: Response,
): Promise<string | undefined> => {
  if (!response.body) {
    return undefined;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let text = '';
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<'timeout'>((resolve) => {
    timer = setTimeout(() => resolve('timeout'), BODY_READ_TIMEOUT_MS);
  });

  try {
    while (text.length < MAX_RESPONSE_BODY_LENGTH) {
      const result = await Promise.race([reader.read(), timeout]);
      if (result === 'timeout' || result.done) {
        break;
      }
      text += decoder.decode(result.value, { stream: true });
    }
  } finally {
    clearTimeout(timer);
    reader.cancel().catch(() => undefined);
  }

  return text.slice(0, MAX_RESPONSE_BODY_LENGTH) || undefined;
};

/**
 * Extracts a string message from an error value for logging purposes.
 *
 * Handles Error objects, strings, fetch Response objects (some SDKs, e.g.
 * SuperTokens, throw the raw Response on non-2xx), and any object with a
 * message property. Falls back to 'Unknown error' if no message can be
 * extracted.
 */
export const formatErrorForLogging = (
  error: unknown,
  fallback = 'Unknown error',
): string => {
  if (typeof error === 'string') {
    return error;
  }

  if (isResponse(error)) {
    const statusText = error.statusText ? ` ${error.statusText}` : '';
    const url = error.url ? ` (${error.url})` : '';
    return `Request failed with status ${error.status}${statusText}${url}`;
  }

  if (
    error &&
    typeof error === 'object' &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return error.message;
  }

  return fallback;
};

export type ResponseErrorDetails = {
  status: number;
  statusText: string;
  url: string;
  body?: string;
};

/**
 * Extracts loggable details from an error that is a fetch Response:
 * HTTP status, statusText, URL, and a best-effort bounded read of the
 * body. Returns undefined for any other error value.
 */
export const extractResponseErrorDetails = async (
  error: unknown,
): Promise<ResponseErrorDetails | undefined> => {
  if (!isResponse(error)) {
    return undefined;
  }

  const details: ResponseErrorDetails = {
    status: error.status,
    statusText: error.statusText,
    url: error.url,
  };

  try {
    const body = await readBodyBounded(error.clone());
    if (body) {
      details.body = body;
    }
  } catch {
    // Body already consumed or unreadable — status/url are still useful
  }

  return details;
};
